/** A printable internal report collects the permitted fault/inspection/repair chain. */
import { useQueries } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { unwrap } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { PersonReference } from "@/features/entities/PersonReference";
import { AssetReference } from "@/features/entities/AssetReference";
import { DocumentFileLink } from "@/features/entities/DocumentFileLink";
import { checklistAnswer } from "@/features/workorders/checklist";
import { Attachments } from "@/features/documents/Attachments";
import { useRequest } from "@/features/requests/queries";
import { Details, ErrorState, StatusBadge } from "@/shared/components/page";
import { Button } from "@/shared/ui/button";
import { ItemName } from "@/shared/components/item-name";
import { formatDateTime, formatMoney } from "@/shared/lib/format";
import type { WorkOrderDetail } from "./queries";

/** Fetch jobs individually scoped to the supervisor; source IDs never grant access. */
export function SupervisorReview({ wo }: { wo: WorkOrderDetail }): ReactElement {
  const { t, i18n } = useTranslation();
  const { can } = useAuth();
  const fault = useRequest(wo.service_request_id ?? "");
  const ids = fault.data?.work_order_ids.filter((id) => id !== wo.id) ?? [];
  const jobs = useQueries({
    queries: ids.map((id) => ({
      queryKey: ["work-order", id],
      queryFn: async () =>
        unwrap(await api.GET("/api/v1/work-orders/{wo_id}", { params: { path: { wo_id: id } } })),
    })),
  });
  const chain = [wo, ...jobs.flatMap((job) => (job.data ? [job.data] : []))];
  return (
    <div className="space-y-5">
      <Button variant="outline" onClick={() => window.print()}>
        {t("workOrders.printReport")}
      </Button>
      {fault.data ? (
        <section>
          <h2 className="font-semibold">
            {fault.data.code} · {fault.data.title}
          </h2>
          <p>{fault.data.description}</p>
          <p>{fault.data.resolution}</p>
          <Attachments entityType="request" entityId={fault.data.id} />
        </section>
      ) : null}
      {jobs.some((job) => job.isError) ? (
        <p role="alert">{t("workOrders.chainUnavailable")}</p>
      ) : null}
      {fault.isError && wo.service_request_id ? <ErrorState error={fault.error} /> : null}
      {chain.map((job) => (
        <section key={job.id} className="space-y-3 rounded-md border p-4">
          <h2 className="font-semibold">
            <Link to="/work-orders/$woId" params={{ woId: job.id }}>
              {job.code} · {job.title}
            </Link>
          </h2>
          <StatusBadge value={job.status} namespace="workOrders.status" />
          <Details
            items={[
              {
                label: t("app.asset"),
                value: (
                  <AssetReference
                    id={job.asset.id}
                    code={job.asset.code}
                    name={job.asset.name}
                    context="work-order"
                  />
                ),
              },
              { label: t("workOrders.actualEnd"), value: formatDateTime(job.actual_end) },
              {
                label: t("workOrders.accepted"),
                value: `${formatDateTime(job.accepted_at)} · ${job.accepted_by_name ?? t("workOrders.system")}`,
              },
              { label: t("workOrders.released"), value: formatDateTime(job.released_at) },
              { label: t("workOrders.completionNotes"), value: job.completion_notes || "—" },
              { label: t("workOrders.diagnosis"), value: job.failure_report?.narrative ?? "—" },
              {
                label: t("workOrders.functionalTest"),
                value: job.functional_test_result
                  ? t(
                      job.functional_test_result === "pass"
                        ? "workOrders.checkPass"
                        : "workOrders.checkFail",
                    )
                  : "—",
              },
              { label: t("workOrders.testEvidence"), value: job.functional_test_notes || "—" },
              { label: t("workOrders.meterAtCompletion"), value: job.meter_at_completion ?? "—" },
              {
                label: t("workOrders.labour"),
                value: job.labour.length
                  ? job.labour.map((entry, index) => (
                      <span key={entry.id}>
                        {index ? "; " : null}
                        <PersonReference
                          id={entry.membership_id}
                          name={entry.performer_name ?? t("workOrders.system")}
                        />
                        : {entry.hours} · {entry.activity}
                        {entry.uncosted ? ` (${t("workOrders.uncosted")})` : ""}
                      </span>
                    ))
                  : "—",
              },
              {
                label: t("workOrders.parts"),
                value: job.parts.length
                  ? job.parts.map((part, index) => (
                      <span key={part.id}>
                        {index ? "; " : null}
                        <ItemName itemId={part.item_id} name={part.item_name} sku={part.item_sku} />
                        : {part.quantity} ({t(`workOrders.partStatus.${part.status}`)})
                      </span>
                    ))
                  : "—",
              },
              ...(can("workorders.cost.view")
                ? [
                    {
                      label: t("workOrders.actual"),
                      value: formatMoney(job.actual_cost, i18n.language),
                    },
                  ]
                : []),
            ]}
          />
          {job.tasks.map((task) => (
            <div key={task.id}>
              <h3>
                {task.sequence}. {task.title} ·{" "}
                <StatusBadge value={task.status} namespace="workOrders.taskStatus" />
              </h3>
              <p>
                {task.notes} · {task.performer_name ?? t("workOrders.system")}
              </p>
              <ul>
                {task.checklist.map((item) => {
                  const evidence = checklistAnswer(item.value);
                  return (
                    <li key={item.id}>
                      {item.prompt}:{" "}
                      {evidence.value === null
                        ? "—"
                        : typeof evidence.value === "boolean"
                          ? t(evidence.value ? "app.yes" : "app.no")
                          : item.response_type === "pass_fail" &&
                              (evidence.value === "pass" || evidence.value === "fail")
                            ? t(
                                evidence.value === "pass"
                                  ? "workOrders.checkPass"
                                  : "workOrders.checkFail",
                              )
                            : String(evidence.value)}
                      {item.passed === false ? ` · ${t("workOrders.checkFail")}` : ""} {item.note}
                      {evidence.photoDocumentId ? (
                        <DocumentFileLink
                          id={evidence.photoDocumentId}
                          versionId={evidence.photoVersionId}
                          label={t("fileEvidence.openEvidence")}
                        />
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
          <Attachments entityType="workorder" entityId={job.id} />
        </section>
      ))}
    </div>
  );
}
